import { useObservable, type CustomerOrder, type Order } from '@talos/kyoko';
import { combineLatest, map, type Observable } from 'rxjs';
import type { BlotterTableOrderFilter } from './useOrderFilter';
import { useRecentCustomerOrders } from './useRecentCustomerOrders';

interface OrdersSubscriptionResponse {
  data: Order[];
}

interface UseCustomerEnrichedOrdersObservableParams {
  dataObservable: Observable<OrdersSubscriptionResponse>;
  filter: BlotterTableOrderFilter;
}

export const useCustomerEnrichedOrdersObservable = ({
  dataObservable,
  filter,
}: UseCustomerEnrichedOrdersObservableParams) => {
  const customerOrderByOrderIDObs = useRecentCustomerOrders(filter);

  return useObservable(
    () =>
      combineLatest([dataObservable, customerOrderByOrderIDObs]).pipe(
        map(([orders, customerOrders]: [OrdersSubscriptionResponse, Map<string, CustomerOrder>]) => {
          const enrichedData = orders.data.map(order => {
            if (order.ParentOrderID && customerOrders.has(order.ParentOrderID)) {
              const customerOrder = customerOrders.get(order.ParentOrderID);
              order.enrichOrderWithCustomerOrder(customerOrder);
            }
            return order;
          });

          return {
            ...orders,
            data: enrichedData,
          };
        })
      ),
    [dataObservable, customerOrderByOrderIDObs]
  );
};
